/**
 * Redesigned media page (views/redesign/pages/resources/media.ejs).
 *
 *  - The filter form (#mediaFilter) shows the cards matching its type, topic
 *    and "newly added" selects, updates the live count, and shows the empty
 *    state when nothing matches. On phones the form is behind a Filter toggle.
 *  - Click-to-load players: nothing from YouTube or Podbean loads until a
 *    play button (.facade) is pressed; it is then replaced by the player
 *    iframe, which takes focus.
 */

import { trackMediaPlay } from '../../utils/trackMediaPlay.js';

const PLAYER_HOSTS = [/^www\.youtube-nocookie\.com$/, /^(.+\.)?podbean\.com$/];

/** True when a card matches the filter values { type, topic, show }. */
export function cardMatches(card, { type, topic, show }) {
    return (type === 'all' || card.type === type)
        && (topic === 'all' || card.topic === topic)
        && (show === 'all' || card.isNew);
}

/** The player URL from a facade's data-src, or '' when it isn't an allowed https host. */
export function safePlayerSrc(src) {
    try {
        const url = new URL(src);
        return url.protocol === 'https:' && PLAYER_HOSTS.some((re) => re.test(url.hostname)) ? url.href : '';
    } catch {
        return '';
    }
}

function setCount(count, shown, total, filtered) {
    const strong = document.createElement('strong');
    if (filtered) {
        strong.textContent = String(shown);
        count.replaceChildren('Showing ', strong, ` of ${total}`);
    } else {
        strong.textContent = String(total);
        count.replaceChildren('Showing all ', strong);
    }
}

function init() {
    const form = document.getElementById('mediaFilter');
    const list = document.getElementById('mediaList');
    if (!form || !list) return;
    const cards = [...list.children];
    const count = document.getElementById('mediaCount');
    const empty = document.getElementById('mediaEmpty');
    const active = document.getElementById('mediaFilterActive');
    const toggle = document.getElementById('mediaFilterToggle');

    function apply() {
        const f = form.elements;
        const values = { type: f.type.value, topic: f.topic.value, show: f.show.value };
        let shown = 0;
        for (const li of cards) {
            const ok = cardMatches({ type: li.dataset.type, topic: li.dataset.topic, isNew: li.dataset.new === '1' }, values);
            li.hidden = !ok;
            if (ok) shown++;
        }
        const on = Object.values(values).filter((v) => v !== 'all').length;
        if (count) setCount(count, shown, cards.length, on > 0);
        if (empty) empty.hidden = shown > 0;
        if (active) active.textContent = on ? `(${on} on)` : '';
    }

    toggle?.addEventListener('click', () => {
        const open = form.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', String(open));
        if (open) form.elements.type.focus();
    });
    form.addEventListener('change', apply);
    form.addEventListener('submit', (e) => e.preventDefault());
    form.addEventListener('reset', () => setTimeout(apply, 0));
    document.getElementById('mediaReset')?.addEventListener('click', () => {
        form.reset();
        setTimeout(() => {
            apply();
            if (toggle && getComputedStyle(toggle).display !== 'none' && !form.classList.contains('is-open')) {
                form.classList.add('is-open');
                toggle.setAttribute('aria-expanded', 'true');
            }
            form.elements.type.focus();
        }, 0);
    });

    list.addEventListener('click', (e) => {
        const card = e.target.closest('.media-card[data-media-id]');
        const item = card ? `media:${card.dataset.mediaId}` : '';
        if (e.target.closest('.media-card__links a')) {
            trackMediaPlay(item, 'external');
            return;
        }
        const btn = e.target.closest('.facade');
        if (!btn) return;
        const src = safePlayerSrc(btn.dataset.src);
        if (!src) return;
        trackMediaPlay(item);
        const frame = document.createElement('iframe');
        frame.src = src;
        frame.title = btn.dataset.title || (btn.dataset.kind === 'audio' ? 'Podcast player' : 'Video player');
        frame.className = 'player-frame' + (btn.dataset.kind === 'audio' ? ' player-frame--audio' : '');
        frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        frame.allowFullscreen = true;
        frame.referrerPolicy = 'strict-origin-when-cross-origin';
        btn.replaceWith(frame);
        frame.focus();
    });
}

if (typeof document !== 'undefined' && document.getElementById('mediaList')) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
}
